// src/data/cardsApi.js
// ─────────────────────────────────────────────────────────────
// Toute la logique qui lit et filtre cards.json.
// Si tu passes à Supabase un jour, tu ne touches qu'à CE fichier.
// Les pages et composants ne savent pas d'où viennent les données.

import ALL_CARDS from './cards.json';

// ─── Utilitaires ─────────────────────────────────────────────

/** Mélange un tableau sans modifier l'original */
export function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

/** Pioche N éléments aléatoires dans un tableau */
export function pickRandom(arr, n) {
  return shuffle(arr).slice(0, n);
}

// ─── Fonction principale de filtrage ─────────────────────────

/**
 * Filtre les cartes selon des critères.
 *
 * @param {Object}  opts
 * @param {string}  [opts.type]          - 'action' | 'verite' | 'punition' | 'date' | 'conseil' | 'ressource'
 * @param {string}  [opts.context]       - 'maison' | 'distance' | 'general'
 * @param {number}  [opts.maxIntensity]  - 1 | 2 | 3  (inclut tous les niveaux ≤ maxIntensity)
 * @param {string}  [opts.pack]          - filtre par pack, ex: 'core'
 * @param {boolean} [opts.hasTarget]     - filtre sur has_target si défini
 * @returns {Array}
 */
export function getCards({ type, context, maxIntensity = 3, pack, hasTarget } = {}) {
  return ALL_CARDS.filter(card => {
    if (type         && card.type      !== type)              return false;
    if (context      && card.context   !== context)           return false;
    if (maxIntensity && card.intensity  > maxIntensity)       return false;
    if (pack         && card.pack      !== pack)              return false;
    if (hasTarget    !== undefined && card.has_target !== hasTarget) return false;
    return true;
  });
}

/**
 * Pioche N cartes aléatoires selon des critères.
 *
 * @param {Object} opts     - mêmes options que getCards()
 * @param {number} [n=1]    - nombre de cartes à piocher
 * @returns {Array}
 */
export function drawCards(opts, n = 1) {
  const pool = getCards(opts);
  return pickRandom(pool, n);
}

/**
 * Pioche UNE carte aléatoire (raccourci pratique).
 * Retourne null si le pool est vide.
 */
export function drawOne(opts) {
  const results = drawCards(opts, 1);
  return results[0] || null;
}

/**
 * Charge les sections de l'accueil : plusieurs cartes par type.
 * Retourne un tableau de sections avec leurs cartes pré-chargées.
 *
 * @param {number} maxIntensity
 * @returns {Array<{ type, context, icon, title, cards }>}
 */
export function getHomeSections(maxIntensity = 2) {
  const SECTIONS = [
    { type: 'action',   context: 'maison',   icon: '♟️', title: 'Actions — en présence',  count: 3 },
    { type: 'verite',   context: 'maison',   icon: '🎭', title: 'Vérités — en présence',  count: 3 },
    { type: 'punition', context: 'maison',   icon: '😈', title: 'Punitions',              count: 3 },
    { type: 'action',   context: 'distance', icon: '💌', title: 'Actions à distance',     count: 3 },
    { type: 'verite',   context: 'distance', icon: '🔮', title: 'Vérités à distance',     count: 3 },
    { type: 'date',     context: 'general',  icon: '🌙', title: 'Idées de dates',         count: 3 },
    { type: 'conseil',  context: 'general',  icon: '💡', title: 'Conseils',               count: 2 },
  ];

  return SECTIONS.map(s => ({
    ...s,
    cards: drawCards({ type: s.type, context: s.context, maxIntensity }, s.count),
  }));
}
